/**
 * Ficha de manutenção do agente nocivo (#458). A tarefa da tela: corrigir a classificação de um
 * agente do catálogo, cuidar das opções de limite e, se for o caso, tirá-lo de uso — nunca
 * excluí-lo. Quem só tem permissão de leitura vê a ficha sem nenhum controle de edição.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { ArrowBack as BackIcon, Tune as ClassificationIcon } from '@mui/icons-material';
import { InfoItem, PageShell, SectionCard } from '../../components';
import { useAcao } from '../../authorization/useVeredito';
import {
  hazardousAgentService,
  type HazardousAgentSheet,
  type HazardousAgentStatus,
  type HazardousAgentUpdateInput,
  type UnhealthyDegree,
} from '../../services/hazardousAgentService';
import { AgentHistorySection } from './components/AgentHistorySection';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { ToleranceLimitsSection } from './components/ToleranceLimitsSection';
import { DEGREE_LABELS } from './degreeLabels';
import { serverMessage } from './errorMessage';

const NO_DEGREE = '';

const parseHygieneStandards = (text: string): string | string[] | null => {
  const items = text.split(',').map((item) => item.trim()).filter(Boolean);
  if (items.length === 0) return null;
  return items.length === 1 ? items[0] : items;
};

export const HazardousAgentEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const agentId = Number(id);
  // Quem não pode atualizar não vê controle de edição (decisão da issue #458); a pergunta de
  // acesso continua sendo a do `useAcao`, que também confere o módulo contratado.
  const canEdit = !useAcao('hazardous_agent.update', 'Editar agente').disabled;

  const [sheet, setSheet] = useState<HazardousAgentSheet | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [degree, setDegree] = useState<string>(NO_DEGREE);
  const [annex, setAnnex] = useState('');
  const [hygiene, setHygiene] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const [confirmingStatus, setConfirmingStatus] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  const [refreshWarning, setRefreshWarning] = useState(false);
  // O que o formulário tinha quando foi preenchido: só o que mudou em relação a isso é enviado.
  const [base, setBase] = useState({ degree: NO_DEGREE, annex: '', hygiene: '' });

  // Resposta de uma busca antiga (outro agente, ou uma busca já superada) é descartada.
  const currentId = useRef(agentId);
  const requestSeq = useRef(0);
  const hasSheet = useRef(false);

  const fillForm = (data: HazardousAgentSheet) => {
    const filled = {
      degree: data.unhealthyDegree ?? NO_DEGREE,
      annex: data.nr15Annex === null ? '' : String(data.nr15Annex),
      hygiene: data.hygieneStandards ?? '',
    };
    setDegree(filled.degree);
    setAnnex(filled.annex);
    setHygiene(filled.hygiene);
    setBase(filled);
  };

  const load = useCallback(
    async (keepDraft = false) => {
      if (agentId !== currentId.current) return;
      const seq = ++requestSeq.current;
      try {
        const data = await hazardousAgentService.getSheet(agentId);
        if (seq !== requestSeq.current) return;
        hasSheet.current = true;
        setSheet(data);
        setFailed(false);
        setRefreshWarning(false);
        if (!keepDraft) fillForm(data);
      } catch {
        if (seq !== requestSeq.current) return;
        // Com a ficha já na tela, a falha vira aviso: só a primeira carga vira tela de erro.
        if (hasSheet.current) setRefreshWarning(true);
        else setFailed(true);
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    },
    [agentId]
  );

  useEffect(() => {
    currentId.current = agentId;
    hasSheet.current = false;
    setSheet(null);
    setLoading(true);
    setFailed(false);
    setRefreshWarning(false);
    setSaved(false);
    setSaveError(null);
    setConfirmingStatus(false);
    setStatusError(null);
    setDegree(NO_DEGREE);
    setAnnex('');
    setHygiene('');
    setBase({ degree: NO_DEGREE, annex: '', hygiene: '' });
    load();
  }, [agentId, load]);

  const openStatusDialog = () => {
    setStatusError(null);
    setConfirmingStatus(true);
  };

  const closeStatusDialog = () => {
    setStatusError(null);
    setConfirmingStatus(false);
  };

  const backToList = () => navigate('/hazardous-agents');

  const afterChange = async () => {
    // Limites e situação mudaram: o que a pessoa digitou na classificação e ainda não salvou fica.
    await load(true);
    setRefreshKey((key) => key + 1);
  };

  const save = async () => {
    const changes: HazardousAgentUpdateInput = {};
    if (degree !== base.degree) {
      changes.unhealthyDegree = degree === NO_DEGREE ? null : (degree as UnhealthyDegree);
    }
    if (annex !== base.annex) changes.nr15Annex = annex.trim() === '' ? null : Number(annex);
    if (hygiene !== base.hygiene) changes.hygieneStandards = parseHygieneStandards(hygiene);

    setSaved(false);
    if (Object.keys(changes).length === 0) {
      setSaveError('Você ainda não alterou nenhum campo.');
      return;
    }

    const savingFor = agentId;
    try {
      setSaving(true);
      setSaveError(null);
      const updated = await hazardousAgentService.update(agentId, changes);
      if (savingFor !== currentId.current) return;
      hasSheet.current = true;
      setSheet(updated);
      fillForm(updated);
      setSaved(true);
      setRefreshKey((key) => key + 1);
    } catch (err: unknown) {
      setSaveError(serverMessage(err, 'Não foi possível salvar as alterações. Tente de novo.'));
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (status: HazardousAgentStatus) => {
    try {
      setStatusBusy(true);
      setStatusError(null);
      await hazardousAgentService.setStatus(agentId, status);
      setConfirmingStatus(false);
      await afterChange();
    } catch (err: unknown) {
      setStatusError(serverMessage(err, 'Não foi possível mudar a situação. Tente de novo.'));
    } finally {
      setStatusBusy(false);
    }
  };

  const breadcrumbs = [
    { label: 'Início', path: '/' },
    { label: 'Agentes Nocivos', path: '/hazardous-agents' },
    { label: sheet?.displayName ?? 'Agente' },
  ];
  const backButton = (
    <Button variant="outlined" size="small" startIcon={<BackIcon />} onClick={backToList}>
      Voltar para a lista
    </Button>
  );

  if (loading) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title="Agente nocivo" actions={backButton}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  if (failed || !sheet) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title="Agente nocivo" actions={backButton}>
        <Alert severity="error">
          Não foi possível abrir este agente. Ele pode não existir mais ou a conexão pode ter falhado.
        </Alert>
      </PageShell>
    );
  }

  const inactive = sheet.status === 'INACTIVE';
  const statusAction = inactive ? (
    <Button variant="outlined" size="small" color="success" onClick={openStatusDialog}>
      Reativar agente
    </Button>
  ) : (
    <Button variant="outlined" size="small" color="warning" onClick={openStatusDialog}>
      Inativar agente
    </Button>
  );

  return (
    <PageShell
      breadcrumbs={breadcrumbs}
      title={sheet.displayName}
      subtitle={`CAS: ${sheet.casNumber ?? '—'} · eSocial: ${sheet.esocialCode ?? '—'}`}
      actions={
        <>
          {backButton}
          {canEdit && statusAction}
        </>
      }
    >
      <Stack spacing={3}>
        {inactive && (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Chip label="Inativo" />
            <Typography variant="body2" color="text.secondary">
              Este agente não aparece na escolha de agentes ao cadastrar riscos.
            </Typography>
          </Stack>
        )}

        {refreshWarning && (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={() => load(true)}>
                Tentar de novo
              </Button>
            }
          >
            Alteração salva, mas não consegui atualizar a ficha.
          </Alert>
        )}

        <SectionCard title="Classificação do agente" icon={<ClassificationIcon />}>
          {canEdit ? (
            <Stack spacing={2} sx={{ maxWidth: 480 }}>
              <TextField
                select
                label="Grau de insalubridade"
                value={degree}
                onChange={(e) => {
                  setDegree(e.target.value);
                  setSaved(false);
                }}
                fullWidth
              >
                <MenuItem value={NO_DEGREE}>Sem grau definido</MenuItem>
                {Object.entries(DEGREE_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Anexo da NR-15"
                type="number"
                value={annex}
                onChange={(e) => {
                  setAnnex(e.target.value);
                  setSaved(false);
                }}
                helperText={
                  sheet.nr15AnnexLabel
                    ? `Anexo atual: ${sheet.nr15AnnexLabel}`
                    : 'Digite o número do anexo, por exemplo: 11. Deixe vazio se não houver.'
                }
                fullWidth
              />
              <TextField
                label="Norma de higiene"
                value={hygiene}
                onChange={(e) => {
                  setHygiene(e.target.value);
                  setSaved(false);
                }}
                helperText="Por exemplo: NHO 01. Para mais de uma, separe por vírgula."
                fullWidth
              />
              {saveError && <Alert severity="error">{saveError}</Alert>}
              {saved && <Alert severity="success">Alterações salvas.</Alert>}
              <Box>
                <Button variant="contained" onClick={save} disabled={saving}>
                  Salvar alterações
                </Button>
              </Box>
            </Stack>
          ) : (
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' } }}>
              <InfoItem
                label="Grau de insalubridade"
                value={sheet.unhealthyDegree ? DEGREE_LABELS[sheet.unhealthyDegree] : 'Sem grau definido'}
              />
              <InfoItem label="Anexo da NR-15" value={sheet.nr15AnnexLabel ?? sheet.nr15Annex} />
              <InfoItem label="Norma de higiene" value={sheet.hygieneStandards} />
            </Box>
          )}
        </SectionCard>

        <ToleranceLimitsSection
          key={`limits-${agentId}`}
          agentId={agentId}
          limits={sheet.toleranceLimits}
          canEdit={canEdit}
          onChanged={afterChange}
        />

        <AgentHistorySection key={`history-${agentId}`} agentId={agentId} refreshKey={refreshKey} />
      </Stack>

      <ConfirmDialog
        open={confirmingStatus}
        title={inactive ? 'Reativar este agente?' : 'Inativar este agente?'}
        message={
          inactive
            ? 'O agente volta a aparecer na escolha de agentes ao cadastrar riscos.'
            : 'O agente deixa de aparecer na escolha de agentes ao cadastrar riscos. Riscos já cadastrados com ele continuam como estão. O agente inativo também deixa de ser reconhecido na importação de riscos por planilha. Você pode reativá-lo quando quiser.'
        }
        confirmLabel={inactive ? 'Sim, reativar' : 'Sim, inativar'}
        confirmColor={inactive ? 'success' : 'warning'}
        loading={statusBusy}
        error={statusError}
        onConfirm={() => changeStatus(inactive ? 'ACTIVE' : 'INACTIVE')}
        onCancel={closeStatusDialog}
      />
    </PageShell>
  );
};

export default HazardousAgentEditPage;
